// DonutChart.jsx
import React from "react";
import PropTypes from "prop-types";
import "./Dashboard.css";

export default function DonutChart({ data }) {
  if (!data || data.length === 0) {
    return <p className="dashboard-no-data-text">No data available.</p>;
  }

  const totalVal = data.reduce((sum, d) => sum + d.value, 0);

  // Fixed sizing - responsive scaling handled by CSS
  const size = 240;
  const center = size / 2;
  const r = 80;
  const innerR = 50;

  const colors = ["#9700EF", "#CC76FF", "#00EF97", "#FFBD00", "#FF6B6B", "#36D7B7"];

  return (
    <div className="chart-wrapper" style={{ position: "relative" }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="chart-svg"
        style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }}
      >
        {data.length === 1 ? (
          (() => {
            const single = data[0];
            const color = colors[0 % colors.length];
            const strokeWidth = r - innerR;
            const circleRadius = (r + innerR) / 2;

            return (
              <circle
                cx={center}
                cy={center}
                r={circleRadius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
              />
            );
          })()
        ) : (
          (() => {
            let cumulativeAngle = -90;

            return data.map((d, i) => {
              const angle = (d.value / totalVal) * 360;
              const startAngle = cumulativeAngle;
              const endAngle = cumulativeAngle + angle;

              const x1 = center + r * Math.cos((startAngle * Math.PI) / 180);
              const y1 = center + r * Math.sin((startAngle * Math.PI) / 180);
              const x2 = center + r * Math.cos((endAngle * Math.PI) / 180);
              const y2 = center + r * Math.sin((endAngle * Math.PI) / 180);
              const x3 = center + innerR * Math.cos((endAngle * Math.PI) / 180);
              const y3 = center + innerR * Math.sin((endAngle * Math.PI) / 180);
              const x4 = center + innerR * Math.cos((startAngle * Math.PI) / 180);
              const y4 = center + innerR * Math.sin((startAngle * Math.PI) / 180);

              const largeArc = angle > 180 ? 1 : 0;
              const pathData = `
                M ${x1} ${y1}
                A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}
                L ${x3} ${y3}
                A ${innerR} ${innerR} 0 ${largeArc} 0 ${x4} ${y4}
                Z
              `.trim();

              cumulativeAngle = endAngle;

              return (
                <path
                  key={i}
                  d={pathData}
                  fill={colors[i % colors.length]}
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth={1}
                />
              );
            });
          })()
        )}
      </svg>

      {/* LEGEND */}
      <div className="donut-legend">
        {data.map((d, i) => {
          const percentage = ((d.value / totalVal) * 100).toFixed(0);
          const displayName = d.name || "Unknown";
          return (
            <div key={i} className="donut-legend-item">
              <div
                className="donut-legend-color-box"
                style={{ background: colors[i % colors.length] }}
              />
              <span style={{ color: "rgba(255,255,255,0.7)" }}>
                {displayName.length > 15 ? displayName.substring(0, 15) + "…" : displayName} — {percentage}%
              </span>
            </div>
          );
        })}
      </div>
      {/* TEXT BELOW LEGEND */}
      <div style={{
        marginTop: '16px',
        fontFamily: 'Helvetica, -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center'
      }}>
        Distribution across all retirements
      </div>
    </div>
  );
}

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
};
